/**
 * Calculates the suggested EBT payment amount to display.
 * Returns the smaller of: SNAP balance or SNAP-eligible total.
 *
 * @param {Object} params - The parameters object
 * @param {number|string} [params.snapBalance] - Available SNAP balance
 * @param {number|string} params.snapEligibleTotal - SNAP-eligible cart total
 * @returns {string} Suggested payment amount formatted to 2 decimal places
 */
export const getSuggestedPaymentAmount = ({ snapBalance, snapEligibleTotal }) => {
    const balance = Number(snapBalance) || 0;
    const eligibleTotal = Number(snapEligibleTotal) || 0;

    if (snapBalance !== undefined && balance < eligibleTotal) {
        return balance.toFixed(2);
    }

    return eligibleTotal.toFixed(2);
};

/**
 * Gets EBT payment amount validation error message (before submitting the form).
 *
 * @param {number} amount - Payment amount to validate
 * @param {number} snapEligibleTotal - Maximum SNAP-eligible total
 * @param {number} [snapBalance] - Available SNAP balance
 * @param {number} [currentAmount] - Current payment amount
 * @param {boolean} [allowZero=false] - Whether to allow 0 as a valid amount
 * @returns {string} Error message if invalid, empty string if valid
 */
export const getAmountErrorMessage = ({
    allowZero = false,
    amount,
    currentAmount = 0,
    snapBalance,
    snapEligibleTotal,
}) => {
    const isInitialPayment = currentAmount <= 0;

    // Handle null/undefined/empty string cases
    if (amount === null || amount === undefined || amount === '' || Number.isNaN(amount)) {
        return 'Please enter a valid amount to apply to your order.';
    }

    // Handle negative amounts
    if (amount < 0) {
        return 'Amount must be greater than 0.';
    }

    // Handle zero case based on allowZero flag
    if (amount === 0) {
        if (!allowZero) {
            return 'Amount must be greater than 0.';
        }
        // If allowZero is true, continue to other validations
    }

    // Check if amount equals current amount
    if (amount === currentAmount) {
        return 'Amount cannot be the same as the current amount.';
    }

    if (isInitialPayment && snapBalance !== undefined && amount > snapBalance) {
        return `Enter an amount equal to or less than your card balance of $${snapBalance?.toFixed(2)}.`;
    }

    if (!isInitialPayment && snapBalance !== undefined && amount > currentAmount + snapBalance) {
        return `Enter an amount equal to or less than $${(currentAmount + snapBalance)?.toFixed(2)}.`;
    }

    if (amount > snapEligibleTotal) {
        return `Enter an amount equal to or less than your SNAP-eligible total of $${snapEligibleTotal?.toFixed(2)}.`;
    }

    return '';
};

/**
 * Formats a date string for EBT balance display.
 *
 * Converts a date string to a user-friendly format showing month, day, and time
 * in 12-hour format (e.g., "Jan 15, 2:30pm"). This function is specific to
 * EBT payment flow balance date formatting and is not intended as a general
 * date utility.
 *
 * @param {string} dateString - The date string to format
 * @returns {string} Formatted date string or empty string if input is invalid
 */
export const formatBalanceDate = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString); // This automatically converts to user's local timezone
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');

    // Convert to 12-hour format
    let displayHours;
    if (hours === 0) {
        displayHours = 12;
    } else if (hours > 12) {
        displayHours = hours - 12;
    } else {
        displayHours = hours;
    }
    const ampm = hours >= 12 ? 'pm' : 'am';

    return `${month} ${day}, ${displayHours}:${minutes}${ampm}`;
};
